import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { motion } from "framer-motion";
import { X, Sparkles, Lock, Flame } from "lucide-react";

export default function WorldThemePanel({ open, conversationId, onClose, onChanged }) {
  const [current, setCurrent] = useState(null);
  const [themes, setThemes] = useState({});
  const [unlocks, setUnlocks] = useState({});
  const [streakMax, setStreakMax] = useState(0);
  const [saving, setSaving] = useState(false);
  const [lockedMsg, setLockedMsg] = useState("");

  useEffect(() => {
    if (!open || !conversationId) return;
    (async () => {
      try {
        const { data } = await api.get(`/conversations/${conversationId}/world`);
        setCurrent(data.theme);
        setThemes(data.themes || {});
        setUnlocks(data.unlocks || {});
        setStreakMax(Number(data.quest_streak_max || 0));
      } catch {}
    })();
  }, [open, conversationId]);

  const pick = async (key, isLocked) => {
    if (isLocked) return;
    setSaving(true);
    setLockedMsg("");
    try {
      const { data } = await api.put(`/conversations/${conversationId}/world`, { theme: key });
      setCurrent(data.theme || key);
      onChanged?.(key, data.config);
      setTimeout(() => onClose?.(), 350);
    } catch (e) {
      setLockedMsg(formatApiError(e));
      setTimeout(() => setLockedMsg(""), 4500);
    }
    setSaving(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="world-panel">
      <div className="bg-white border-hard shadow-hard-lg rounded-2xl w-full max-w-xl p-6 slide-up max-h-[85vh] overflow-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-2xl flex items-center gap-2"><Sparkles strokeWidth={3} className="w-6 h-6" /> Sohbet Dünyası</h2>
          <button onClick={onClose} data-testid="world-close-btn" className="w-9 h-9 border-hard-2 rounded-lg flex items-center justify-center press-btn">
            <X strokeWidth={3} className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-[#555] mb-3">Bu sohbete birlikte yaşayacağınız bir dünya seçin — arka plan, animasyon ve dekorlar hemen değişir.</p>

        {streakMax > 0 && (
          <div className="mb-4 inline-flex items-center gap-2 bg-[#FF4E00] text-white border-hard-2 rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-widest" data-testid="world-streak-info">
            <Flame strokeWidth={3} className="w-3.5 h-3.5" /> En uzun görev serisi: {streakMax} gün
          </div>
        )}

        {lockedMsg && (
          <div className="mb-3 border-hard-2 bg-[#FFE7CC] rounded-xl px-3 py-2 text-xs font-bold" data-testid="world-locked-msg">
            {lockedMsg}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.entries(themes).map(([key, t]) => {
            const active = current === key;
            const themeLocked = Boolean(t.locked);
            const unlockKey = unlocks[key];
            const isLocked = themeLocked && unlockKey !== true;
            return (
              <motion.button
                key={key}
                whileHover={{ y: isLocked ? 0 : -3 }}
                onClick={() => pick(key, isLocked)}
                data-testid={`world-theme-${key}`}
                disabled={saving || isLocked}
                className={`relative border-hard rounded-2xl overflow-hidden text-left shadow-hard-sm transition-opacity ${active ? "ring-4 ring-[#0A0A0A]" : ""} ${isLocked ? "opacity-70 cursor-not-allowed" : "press-btn"}`}
                style={{ background: t.bg }}
              >
                <div className="p-4 h-24 flex items-end">
                  <div className="w-full flex items-center justify-between">
                    <span className="font-display text-lg" style={{ color: t.accent }}>{t.label}</span>
                    {active && <span className="text-xs font-black bg-white border-hard-2 rounded-full px-2 py-0.5">AKTİF</span>}
                    {!active && themeLocked && !isLocked && (
                      <span className="text-[10px] font-black bg-[#CCFF00] border-hard-2 rounded-full px-2 py-0.5 uppercase" data-testid={`world-theme-${key}-unlocked`}>Açıldı</span>
                    )}
                  </div>
                </div>
                {isLocked && (
                  <div className="absolute inset-0 bg-black/45 flex items-center justify-center p-3" data-testid={`world-theme-${key}-lock`}>
                    <div className="text-center text-white">
                      <Lock strokeWidth={3} className="w-5 h-5 mx-auto mb-1" />
                      <div className="text-[10px] uppercase font-black tracking-widest leading-tight">
                        {t.unlock_hint || "Kilitli"}
                      </div>
                    </div>
                  </div>
                )}
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export const WORLD_FALLBACK = { bg: "#F9F8F6", accent: "#0A0A0A", particles: "" };
